import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: (
      <>
        <p>
          Zenith360 HR (“Zenith360”, “we”, “our”, “us”) is a multi-tenant human-resources platform operated by
          <b> ADB Consulting LLP</b>, an Indian limited-liability partnership with its registered office in India.
          You can reach us at <a href="mailto:hello@zenith360-hr.com" className="text-adb-navy underline">hello@zenith360-hr.com</a>.
        </p>
        <p>
          This policy explains what personal data Zenith360 collects, why we collect it, how we use and share it, how
          long we keep it, and the rights you have. It applies to the Zenith360 web portal at
          <b> https://zenith360-hr.com</b>, the Zenith360 HR mobile applications (Android and iOS), and every
          Zenith360 API.
        </p>
      </>
    ),
  },
  {
    id: "data-controller",
    title: "2. Who is the data controller?",
    body: (
      <>
        <p>
          Zenith360 acts as the <b>data controller</b> only for the personal data we collect about our direct
          customers (the people who sign up for a Zenith360 subscription — administrators, HR agency owners, group
          administrators).
        </p>
        <p>
          For all other personal data processed inside Zenith360 — employees of our customers, candidates, clients,
          project team members — Zenith360 acts as a <b>data processor</b>. Your employer / HR agency is the data
          controller and decides what data is entered, retained, deleted, and how long we retain it. If you have
          questions about your own personnel record, please contact your employer’s HR team first.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "3. Categories of personal data we collect",
    body: (
      <>
        <p>Depending on how you use Zenith360, we may process the following categories of personal data:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Identity &amp; contact:</b> name, employee code, email, phone, address, date of birth.</li>
          <li><b>Employment:</b> designation, department, joining date, reporting manager, salary structure, PF / ESIC / PAN, bank account (for payroll).</li>
          <li><b>Authentication:</b> hashed password, session tokens, IP address, browser / device fingerprint.</li>
          <li><b>Attendance &amp; location:</b> geo-coordinates when you check in / out, coarse-address reverse-geocoded via OpenStreetMap, working-hours computations.</li>
          <li><b>Biometric (selfie):</b> baseline enrollment photograph and every live check-in photo; an AI-generated match score for each check-in.</li>
          <li><b>Trip &amp; vehicle:</b> GPS waypoints during an active trip, mode of travel, vehicle number, computed distance, anomaly flags, per-km fuel amount.</li>
          <li><b>Documents:</b> ID proofs, offer / appointment letters, payslips, timesheets, reimbursement receipts you upload.</li>
          <li><b>Communications:</b> in-app messages, kudos, feedback, survey responses, support tickets.</li>
          <li><b>Device &amp; push:</b> mobile OS version, device model, Firebase Cloud Messaging token, app locale.</li>
          <li><b>Financial (agency / group billing):</b> subscription tier, seat count, Razorpay payment token (Razorpay stores the actual card / UPI data — we do not).</li>
        </ul>
      </>
    ),
  },
  {
    id: "why-we-collect",
    title: "4. Why we collect it (purposes &amp; legal bases)",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Contract performance</b> — providing the HR workflows your employer has subscribed to (attendance, payroll, leaves, reimbursements, KPIs, ATS, LMS).</li>
          <li><b>Legitimate interest</b> — fraud prevention (face-match, anomaly detection on trips, audit logs).</li>
          <li><b>Legal obligation</b> — statutory compliance (PF, ESIC, PT, TDS, labour-law audits, retention of payroll records).</li>
          <li><b>Consent</b> — camera + background-location permissions on the mobile app, marketing communications (which you can opt out of anytime).</li>
        </ul>
      </>
    ),
  },
  {
    id: "who-we-share-with",
    title: "5. Who we share it with",
    body: (
      <>
        <p>We do not sell your personal data. We share it only with:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your employer (the data controller) and their authorised administrators.</li>
          <li>Approved sub-processors listed below:
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li><b>MongoDB Atlas</b> (database hosting — India region).</li>
              <li><b>Emergent Object Storage</b> (uploads: selfies, receipts, offer letters).</li>
              <li><b>Emergent LLM Key</b> (server-side calls to Anthropic Claude, OpenAI, Google Gemini for AI features).</li>
              <li><b>Razorpay</b> (payment processing for agency / group subscriptions).</li>
              <li><b>Firebase Cloud Messaging</b> (push notification delivery only — no message content is retained).</li>
              <li><b>OpenStreetMap Nominatim</b> (reverse-geocoding of check-in coordinates to a human-readable address).</li>
            </ul>
          </li>
          <li>Statutory / government authorities where legally required (e.g. PF / ESIC audits, court orders).</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-long",
    title: "6. How long we retain data",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Active employee record</b> — for the duration of employment plus 7 years (statutory payroll retention).</li>
          <li><b>Check-in selfies (live)</b> — 90 days rolling window unless flagged for HR review.</li>
          <li><b>Baseline selfie</b> — for the duration of employment; deleted within 30 days of separation.</li>
          <li><b>GPS trip waypoints</b> — 12 months.</li>
          <li><b>Audit logs</b> — 3 years (fraud-investigation retention).</li>
          <li><b>Subscription / billing data</b> — 7 years (India tax retention).</li>
        </ul>
        <p className="mt-2 text-xs text-zinc-500">
          Your employer may configure shorter retention windows in their tenant settings. Where they do, the shorter
          window wins.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "7. Your rights",
    body: (
      <>
        <p>Subject to applicable law (DPDP Act 2023 in India, GDPR in the EEA, and equivalent laws elsewhere), you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Ask for a copy of the personal data we hold about you.</li>
          <li>Correct inaccurate or incomplete data.</li>
          <li>Request deletion (see <Link to="/delete-account" className="text-adb-navy underline">/delete-account</Link>).</li>
          <li>Object to processing based on legitimate interest.</li>
          <li>Withdraw consent for camera / location / marketing anytime from your device settings.</li>
          <li>Lodge a complaint with your local data-protection authority.</li>
        </ul>
        <p>To exercise any of these rights, email <a href="mailto:privacy@zenith360-hr.com" className="text-adb-navy underline">privacy@zenith360-hr.com</a>. We respond within 30 days.</p>
      </>
    ),
  },
  {
    id: "security",
    title: "8. Security",
    body: (
      <>
        <p>
          All data is encrypted in transit (TLS 1.2+). Passwords are hashed with bcrypt. Access to production
          infrastructure is limited to a small on-call team using single-sign-on with hardware-key 2FA. We keep
          audit logs of every admin action for 3 years. In the unlikely event of a personal-data breach we will
          notify affected users and the appropriate regulator within 72 hours of confirming it.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "9. Children",
    body: (
      <p>Zenith360 is a workplace product. It is not intended for anyone under 18, and we do not knowingly collect data from children.</p>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to this policy",
    body: (
      <p>
        If we make a material change to this policy, we will notify direct customers by email at least 14 days
        before the change takes effect. The date at the top of this page is the last time this policy was updated.
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
      <Link to="/" className="text-xs text-zenith-teal font-bold uppercase tracking-widest">← Back to Zenith360 HR</Link>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy mt-4">Privacy Policy</h1>
      <div className="text-xs text-zinc-500 mt-2">Last updated: 28 February 2026 · Version 1.0</div>

      <div className="prose prose-zinc mt-8 space-y-6 text-[15px] leading-relaxed text-zinc-700">
        <p>
          At Zenith360 HR we take privacy seriously — it’s the foundation of the trust HR teams place in us. This
          policy is written to be readable, not legalistic. If anything is unclear, please email us at
          <b> privacy@zenith360-hr.com</b> and we’ll clarify or fix it.
        </p>
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id}>
            <h2 className="font-display text-xl font-bold text-adb-navy mt-6 mb-2">{s.title}</h2>
            {s.body}
          </section>
        ))}

        <hr className="border-zinc-200 my-8" />
        <p className="text-xs text-zinc-500">
          Zenith360 HR is a product of ADB Consulting LLP · Registered in India · Contact:{" "}
          <a href="mailto:hello@zenith360-hr.com" className="text-adb-navy underline">hello@zenith360-hr.com</a>
        </p>
      </div>
    </div>
  );
}
